/**
 * PHASE 10 — satellite support architecture (reserved, no fake data).
 *
 * The object model reserves the SATELLITE type, but there is NO TLE data
 * source in this app yet: no TLE file, no propagation service, and no API
 * key wiring. Per project rules, satellites must never be faked: rather
 * than drawing invented positions, the app reports the missing data source
 * explicitly in the diagnostics overlay.
 *
 * This module defines the provider interface the future TLE pipeline will
 * implement (fetch TLE set -> SGP4 propagation -> topocentric J2000 RA/Dec,
 * same frame as stars/planets) and ships a NoSatelliteProvider whose
 * status() is the honest "SATELLITE DATA SOURCE MISSING" report.
 */

import type { ObserverLocation, ObserverTime } from "../astro/types";

export const SATELLITE_DATA_SOURCE_MISSING = "SATELLITE DATA SOURCE MISSING";

export type SatelliteSourceStatus = "configured" | "missing";

export interface Satellite {
  /** Searchable id, e.g. "SAT:25544" for the ISS. */
  id: string;
  name: string;
  /** NORAD catalogue number. */
  noradId: number;
}

export interface SatelliteProvider {
  readonly id: string;
  /** True when a real TLE source is configured (this app: always false). */
  available(): boolean;
  status(): SatelliteSourceStatus;
  /**
   * Satellites whose topocentric position is known for observer/time.
   * An empty array means "no data source": callers must not invent
   * positions and must surface the missing-source report instead.
   */
  list(observer: ObserverLocation, time: ObserverTime): readonly Satellite[];
}

/** Honest no-data provider — the only satellite provider in the app. */
export class NoSatelliteProvider implements SatelliteProvider {
  readonly id = "none";

  available(): boolean {
    return false;
  }

  status(): SatelliteSourceStatus {
    return "missing";
  }

  list(): readonly Satellite[] {
    return [];
  }
}

/** Human-readable report for the diagnostics overlay / info card. */
export function satelliteDataSourceReport(provider: SatelliteProvider): string {
  if (provider.available()) return `satellites: ${provider.id} TLE source configured`;
  return `${SATELLITE_DATA_SOURCE_MISSING} (no TLE feed or SGP4 propagation configured)`;
}
